import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/useAuth'
import { Eye, EyeOff } from 'lucide-react'
import { validateEmail, validatePassword, validateName, rateLimiter } from '@/utils/inputValidation'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'

const RegisterForm = () => {
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    accountType: 'business', // 'business' or 'customer'
    referralCode: '',
    inviteToken: '' // Track invite token
  })
  const [inviteLeadName, setInviteLeadName] = useState<string | null>(null)
  
  // Set account type from URL parameter and check for invite token
  useEffect(() => {
    const typeParam = searchParams.get('type')
    if (typeParam === 'customer' || typeParam === 'business') {
      setFormData(prev => ({ ...prev, accountType: typeParam }))
    }
    
    // Check for invite token
    const inviteParam = searchParams.get('invite')
    if (inviteParam) {
      setFormData(prev => ({ ...prev, inviteToken: inviteParam, accountType: 'customer' }))
      validateInviteToken(inviteParam)
    }
  }, [searchParams])
  
  // Validate invite token and get lead data
  const validateInviteToken = async (token: string) => {
    try {
      const { data, error } = await supabase
        .from('referral_leads')
        .select('first_name, last_name, tenants(name)')
        .eq('invite_token', token)
        .single()
        
      if (!error && data) {
        const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ')
        const tenantName = data.tenants?.name || 'diesem Unternehmen'
        setInviteLeadName(tenantName)
      }
    } catch (err) {
      console.error('Error validating invite token:', err)
    }
  }
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  
  const { signUp } = useAuth()

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    // Email validation
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    }
    
    // Company name validation
    if (!validateName(formData.companyName)) {
      newErrors.companyName = 'Unternehmensname darf nur Buchstaben, Bindestriche und Apostrophe enthalten (max. 50 Zeichen)'
    }
    
    // Password validation
    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0] // Show first error
    }
    
    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein'
    }
    
    // Rate limiting
    const rateLimitKey = `register_${formData.email}`
    if (!rateLimiter.isAllowed(rateLimitKey, 3)) { // Stricter limit for registration
      const timeToReset = rateLimiter.getTimeToReset(rateLimitKey)
      newErrors.rate = `Zu viele Registrierungsversuche. Versuchen Sie es in ${Math.ceil(timeToReset / 60000)} Minuten erneut.`
    }
    
    return newErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    
    setErrors({})
    setIsLoading(true)
    
    try {
      // Check server-side rate limiting for signup
      const { data: securityCheck, error: securityError } = await supabase.functions.invoke('auth-security', {
        body: {
          action: 'signup_attempt',
          identifier: formData.email
        }
      })

      if (securityError || !securityCheck?.allowed) {
        setErrors({ submit: securityCheck?.message || 'Sicherheitsprüfung fehlgeschlagen' })
        return
      }

      // Different registration paths based on account type
      if (formData.accountType === 'business') {
        // Business registration - create tenant and admin via public register-business function
        console.log('🏢 Business registration - creating tenant and admin')
        
        const { data: registrationData, error: registrationError } = await supabase.functions.invoke('register-business', {
          body: {
            companyName: formData.companyName.trim(),
            adminEmail: formData.email,
            adminPassword: formData.password,
            slug: formData.companyName.toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '')
          }
        })

        if (registrationError || !registrationData?.success) {
          console.error('❌ Business registration error:', registrationError)
          throw new Error(registrationData?.error || registrationError?.message || 'Unternehmen konnte nicht erstellt werden')
        }

        console.log('✅ Business account created successfully')
        
      } else {
        // Customer registration - normal signup process
        console.log('👤 Customer registration')
        
        const registrationResult = await signUp(formData.email, formData.password, {
          company_name: formData.companyName.trim(),
          full_name: formData.companyName.trim(),
          accountType: formData.accountType,
          referral_code: formData.referralCode.trim() || undefined
        })
        
        // If registration successful and invite token present, update referral lead
        if (formData.inviteToken && registrationResult) {
          try {
            // Get the newly created customer
            const { data: customerData } = await supabase
              .from('customers')
              .select('id')
              .eq('user_id', registrationResult.user?.id)
              .single()
            
            if (customerData) {
              // Update referral lead with conversion info
              await supabase
                .from('referral_leads')
                .update({
                  status: 'kunde',
                  converted_to_customer_id: customerData.id,
                  converted_at: new Date().toISOString()
                })
                .eq('invite_token', formData.inviteToken)
              
              // Update customer with invite token
              await supabase
                .from('customers')
                .update({ invited_via_token: formData.inviteToken })
                .eq('id', customerData.id)
              
              console.log('✅ Referral lead converted to customer')
            }
          } catch (conversionError) {
            console.error('Error updating referral lead conversion:', conversionError)
            // Don't block registration if conversion update fails
          }
        }
      }
    } catch (error) {
      // Error handling is done in useAuth hook for customer registration
      // For business registration, we handle it here
      console.error('Registration error:', error)
      setErrors({ 
        submit: error instanceof Error ? error.message : 'Registrierung fehlgeschlagen' 
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {inviteLeadName && (
        <div className="p-3 text-sm bg-primary/10 border border-primary/20 rounded-md">
          <p className="font-medium">Sie wurden eingeladen von: {inviteLeadName}</p>
        </div>
      )}
      
      {errors.rate && (
        <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
          {errors.rate}
        </div>
      )}
      
      <div className="space-y-2">
        <Label>Kontotyp</Label>
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant={formData.accountType === 'business' ? 'default' : 'outline'}
            onClick={() => handleInputChange('accountType', 'business')}
            className="justify-start"
          >
            Unternehmen
          </Button>
          <Button
            type="button"
            variant={formData.accountType === 'customer' ? 'default' : 'outline'}
            onClick={() => handleInputChange('accountType', 'customer')}
            className="justify-start"
          >
            Kunde
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="companyName">
          {formData.accountType === 'business' ? 'Unternehmensname' : 'Vollständiger Name'}
        </Label>
        <Input
          id="companyName"
          type="text"
          placeholder={formData.accountType === 'business' ? 'Ihr Unternehmen' : 'Ihr vollständiger Name'}
          value={formData.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          required
          aria-invalid={errors.companyName ? 'true' : 'false'}
        />
        {errors.companyName && (
          <p className="text-sm text-destructive">{errors.companyName}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="referralCode">Empfehlungscode (optional)</Label>
        <Input
          id="referralCode"
          type="text"
          placeholder="Geben Sie einen Empfehlungscode ein"
          value={formData.referralCode}
          onChange={(e) => handleInputChange('referralCode', e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Haben Sie einen Empfehlungscode? Geben Sie ihn hier ein, um Vorteile zu erhalten.
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">E-Mail-Adresse</Label>
        <Input
          id="email"
          type="email"
          placeholder="ihre@email.de"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          required
          aria-invalid={errors.email ? 'true' : 'false'}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Passwort</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Sicheres Passwort erstellen"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            required
            aria-invalid={errors.password ? 'true' : 'false'}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Mindestens 8 Zeichen mit Groß-, Kleinbuchstaben und Zahlen
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Passwort wiederholen"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            required
            aria-invalid={errors.confirmPassword ? 'true' : 'false'}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        {errors.confirmPassword && (
          <p className="text-sm text-destructive">{errors.confirmPassword}</p>
        )}
      </div>

      <Button 
        type="submit" 
        className="w-full bg-gradient-to-r from-primary to-primary-glow hover:shadow-glow transition-all duration-300"
        disabled={isLoading}
      >
        {isLoading ? 'Registrierung läuft...' : 'Konto erstellen'}
      </Button>
    </form>
  )
}

export default RegisterForm